import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import SoCIntegrationsBannerImage from "../../../../assets/services/asic-design/asicDesign3.png";
import ProfileImage from "../../../../assets/blogpages/person.png";
import "./SocIntegrations.css";
import { AnimatePresence, motion } from "framer-motion";

// Simple highlight component: wraps matches in <mark>
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? <mark key={i}>{part}</mark> : part
  );
};

const SoCIntegrations = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const closeOverlay = () => setShowPopup(false);

  // toggle reply‐forms per comment
  const [replyVisible, setReplyVisible] = useState({});
  const toggleReply = (id) =>
    setReplyVisible(prev => ({ ...prev, [id]: !prev[id] }));

  // refs for scroll‐to‐section
  const sectionRefs = {
    intro: useRef(null),
    useCases: useRef(null),
    interconnect: useRef(null),
    protocol: useRef(null),
    axi: useRef(null),
    cdc: useRef(null),
    memory: useRef(null),
    verification: useRef(null),
    tradeoffs: useRef(null),
    security: useRef(null),
    trends: useRef(null),
    comments: useRef(null),
  };

  const scrollTo = (ref) =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  const handleSearch = () => setSearchTerm(searchInput.trim());
  useEffect(() => {
    if (!searchTerm) return;
    const firstMark = document.querySelector("mark");
    firstMark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  // fade‐in/out on scroll
  useEffect(() => {
    const io = new IntersectionObserver(
      entries =>
        entries.forEach(e =>
          e.isIntersecting
            ? e.target.classList.add("visible")
            : e.target.classList.remove("visible")
        ),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // auto-dismiss popup
  useEffect(() => {
    if (showPopup) {
      const t = setTimeout(() => setShowPopup(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showPopup]);

  const handleReplySubmit = e => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };
  const handleCommentSubmit = e => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };

  return (
    <div className="blog-post-page soc-integrations">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>SoC Integration Strategies</h1>
          <div className="breadcrumb">
            <Link to="/services/asic-design">Services</Link> | SoC Integrations
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            type="text"
            placeholder="Search keyword..."
            className="search-input"
          />
          <button onClick={handleSearch} className="search-button">Search</button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    useCases: "Use Cases",
                    interconnect: "Interconnect Architectures",
                    protocol: "Protocol Bridges",
                    axi: "AXI & AMBA",
                    cdc: "Clock Domain Crossing",
                    memory: "Memory Subsystems",
                    verification: "SoC Verification",
                    tradeoffs: "Power/Perf Trade-offs",
                    security: "Security Integration",
                    trends: "Future Trends",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="blog-content-container">
        {/* Left Column */}
        <div className="blog-left-column">
          <img
            className="post-main-image fade-section"
            src={SoCIntegrationsBannerImage}
            alt="SoC Integrations"
          />

          <div className="post-info-row fade-section">
            <span className="post-author">By John Doe</span>
            <span className="post-category">Category: SoC Design</span>
            <button className="comment-link" onClick={() => scrollTo(sectionRefs.comments)}>
              2 Comments
            </button>
          </div>

          {/* 1. Introduction */}
          <h2 ref={sectionRefs.intro} className="fade-section">Introduction to SoC Integrations</h2>
          <p className="fade-section">
            <Highlight
              text="System-on-Chip (SoC) integration unites diverse IP blocks—CPU cores, DSPs, accelerators, memory controllers—into a cohesive silicon system. This orchestration requires precise planning of interfaces, clocks, resets, and power domains to ensure correct functionality at scale."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Effective integration streamlines verification, minimizes latency between blocks, and balances performance, power, and area constraints. Today’s complex SoCs demand robust methodologies to manage hundreds of interdependent modules."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Integration teams coordinate cross-functional flows: software bring-up, hardware validation, and physical implementation. A clear integration strategy reduces risk and accelerates time-to-market in competitive markets like mobile, automotive, and AI."
              query={searchTerm}
            />
          </p>

          {/* 2. Use Cases */}
          <h2 ref={sectionRefs.useCases} className="fade-section">Key Use Cases</h2>
          <ul className="fade-section">
            {[
              "Mobile SoCs: CPU + GPU + DSP + modem",
              "Automotive ADAS: sensor fusion + vision processor",
              "AI Accelerators: NPU + memory controller + DMA",
              "IoT Gateways: MCU + connectivity IP + security",
              "5G Baseband: PHY + MAC + RF interfaces"
            ].map(item => (
              <li key={item}>
                <Highlight text={item} query={searchTerm} />
              </li>
            ))}
          </ul>
          <p className="fade-section">
            <Highlight
              text="Each use case has unique integration challenges: latency requirements in ADAS, power budgets in mobile, security protocols in IoT, and throughput demands in 5G."
              query={searchTerm}
            />
          </p>

          {/* 3. Interconnect Architectures */}
          <h2 ref={sectionRefs.interconnect} className="fade-section">Interconnect Architectures</h2>
          <p className="fade-section">
            <Highlight
              text="Traditional shared bus architectures (AMBA AHB, Wishbone) suffice for simple systems. Complex SoCs leverage Networks-on-Chip (NoC) with packet-based switching, virtual channels, and QoS prioritization to scale bandwidth and reduce contention."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="NoC topologies—mesh, ring, tree—are selected based on latency, area, and power trade-offs. Controllers map masters and slaves onto the mesh to optimize path length and minimize hop count."
              query={searchTerm}
            />
          </p>

          {/* 4. Protocol Bridges */}
          <h2 ref={sectionRefs.protocol} className="fade-section">Protocol Bridges</h2>
          <p className="fade-section">
            <Highlight
              text="Bridges convert between disparate protocols—AXI to APB, USB-to-AXI, PCIe-to-NoC—facilitating IP reuse. Each bridge must handle differing data widths, flow control, and timing requirements."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Well-designed bridges include FIFOs, width adapters, and backpressure mechanisms. Formal property checks guarantee that data integrity is preserved across protocol domains."
              query={searchTerm}
            />
          </p>

          {/* 5. AXI & AMBA */}
          <h2 ref={sectionRefs.axi} className="fade-section">AXI & AMBA Integration</h2>
          <ul className="fade-section">
            {[
              "AXI4: high‐performance memory‐mapped interface",
              "AXI4-Lite: control and register access",
              "AXI4-Stream: unidirectional data streaming",
            ].map(item => (
              <li key={item}>
                <Highlight text={item} query={searchTerm} />
              </li>
            ))}
          </ul>
          <p className="fade-section">
            <Highlight
              text="AMBA protocols provide standardized handshakes and burst capabilities. Compliance with AXI ensures compatibility across IPs from different vendors."
              query={searchTerm}
            />
          </p>

          {/* 6. Clock Domain Crossing */}
          <h2 ref={sectionRefs.cdc} className="fade-section">Clock Domain Crossing</h2>
          <p className="fade-section">
            <Highlight
              text="Multiple independent clock domains are common in SoCs. Safe CDC requires synchronizers, FIFO-based elastic buffers, or handshake protocols to prevent metastability and data loss."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="CDC verification combines lint checks, static timing exceptions (false path declarations), and dynamic simulation with toggling patterns to catch corner-case failures."
              query={searchTerm}
            />
          </p>

          {/* 7. Memory Subsystems */}
          <h2 ref={sectionRefs.memory} className="fade-section">Memory Subsystem Integration</h2>
          <p className="fade-section">
            <Highlight
              text="SoCs integrate multiple memory types: SRAM, DRAM controllers (DDR4/LPDDR5), on-chip ROM, and non-volatile caches. Each requires careful floorplanning and PHY calibration."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Arbitration between masters, QoS scheduling, and traffic shaping ensure real-time performance, particularly in multimedia and networking applications."
              query={searchTerm}
            />
          </p>

          {/* 8. SoC Verification Flows */}
          <h2 ref={sectionRefs.verification} className="fade-section">SoC Verification Flows</h2>
          <p className="fade-section">
            <Highlight
              text="Top-level verification uses UVM-based environments driving realistic test scenarios, software-driven tests on ISS, and hardware-assisted emulation for full-SoC regression at speed."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Coverage closure tracks module, interface, and corner-case scenarios. Emulation platforms accelerate bring-up and software integration before silicon availability."
              query={searchTerm}
            />
          </p>

          {/* 9. Power/Performance Trade-offs */}
          <h2 ref={sectionRefs.tradeoffs} className="fade-section">Power & Performance Trade-offs</h2>
          <p className="fade-section">
            <Highlight
              text="Balancing performance and power in SoC integration involves DVFS, clock gating, and careful floorplan partitioning to localize high‐activity regions."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Performance-critical paths may be placed in fast voltage islands, while less critical logic resides in low-power domains to minimize energy per operation."
              query={searchTerm}
            />
          </p>

          {/* 10. Security Integration */}
          <h2 ref={sectionRefs.security} className="fade-section">Security & Trust Integration</h2>
          <p className="fade-section">
            <Highlight
              text="SoC security integrates hardware root-of-trust, secure enclaves, and bus monitors to detect unauthorized access. TrustZone and PUF-based key storage provide strong protection against software attacks."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Secure boot sequences and authenticated firmware loaders ensure only trusted code executes, protecting IP and customer data."
              query={searchTerm}
            />
          </p>

          {/* 11. Future Trends */}
          <h2 ref={sectionRefs.trends} className="fade-section">Future Trends in SoC Integration</h2>
          <p className="fade-section">
            <Highlight
              text="Chiplet-based integration and 2.5D/3D stacking will shift SoC integration towards package-level assembly, requiring new verification and thermal management approaches."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="AI-driven integration tools will automate interconnect generation, optimization of QoS, and timing closure across heterogeneous blocks."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Standardized metadata schemas will enable seamless IP exchange and faster assembly of custom SoCs in the cloud."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>2 Comments</h3>

            {/* Comment 1 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Chip Integrator</span>
                    <span className="comment-date">April 24, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Very thorough on NoC and chiplet integration—thanks for the deep dive!
              </p>
              <button className="reply-button" onClick={() => toggleReply("chip")}>
                Reply
              </button>
              {replyVisible["chip"] && (
                <div className="leave-comment">
                  <h3>Reply to Chip Integrator</h3>
                  <form onSubmit={handleReplySubmit}>
                    <div className="form-group">
                      <label>Name</label>
                      <input type="text" placeholder="Your name..." required />
                    </div>
                    <div className="form-group">
                      <label>Message</label>
                      <textarea placeholder="Your reply..." required />
                    </div>
                    <button type="submit" className="send-message-btn">Reply</button>
                  </form>
                </div>
              )}
            </div>

            {/* Comment 2 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">System Architect</span>
                    <span className="comment-date">April 25, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Great coverage of security integration—essential in modern SoCs.
              </p>
              <button className="reply-button" onClick={() => toggleReply("architect")}>
                Reply
              </button>
              {replyVisible["architect"] && (
                <div className="leave-comment">
                  <h3>Reply to System Architect</h3>
                  <form onSubmit={handleReplySubmit}>
                    <div className="form-group">
                      <label>Name</label>
                      <input type="text" placeholder="Your name..." required />
                    </div>
                    <div className="form-group">
                      <label>Message</label>
                      <textarea placeholder="Your reply..." required />
                    </div>
                    <button type="submit" className="send-message-btn">Reply</button>
                  </form>
                </div>
              )}
            </div>

            {/* Leave a comment */}
            <div className="leave-comment fade-section">
              <h3>Leave a comment</h3>
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here..." required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here..." required />
                </div>
                <button type="submit" className="send-message-btn">Send Message</button>
              </form>
            </div>
          </div>
        </div>

        {/* Desktop Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              type="text"
              placeholder="Search keyword..."
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button">Search</button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction",
                      useCases: "Use Cases",
                      interconnect: "Interconnect Architectures",
                      protocol: "Protocol Bridges",
                      axi: "AXI & AMBA",
                      cdc: "Clock Domain Crossing",
                      memory: "Memory Subsystems",
                      verification: "SoC Verification",
                      tradeoffs: "Power/Perf Trade-offs",
                      security: "Security Integration",
                      trends: "Future Trends",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Thank You Popup */}
        <AnimatePresence>
          {showPopup && (
            <motion.div
              className="thank-you-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
            >
              <motion.div
                className="thank-you-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="checkmark-container">
                  <motion.svg
                    className="checkmark"
                    viewBox="0 0 52 52"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
                    <path
                      d="M16 27.5L23 34.5L36 19.5"
                      stroke="#007bff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </div>
                <h3>Thank you for the feedback!</h3>
                <p>We appreciate your comment.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SoCIntegrations;